import React, { useContext, useState } from 'react';
import Style from './Style.module.css';
import { TiTick } from "react-icons/ti";
import { UserContext } from '../../../Contexts/UserContext';
import authInstance from '../../../instance/AuthInstance';
import { PiCurrencyInrBold } from "react-icons/pi";
import RazorpayButton from '../../PaymentGateway/RazorpayButton/Razorpay';
import { FaLock, FaUnlock } from "react-icons/fa";
import { toast } from 'react-toastify';

const Plancard = ({ item, userPostCount = 0 }) => {
    const user = useContext(UserContext);
    const { User } = user;
    const [isActivating, setIsActivating] = useState(false);

    // Unlock rules based on post count
    const unlockRules = {
        "Silver Plan": 10,
        "Golden plan": 20,
        "Platinum Plan": 40
    };

    // Check if plan is unlockable (has unlock rule)
    const isUnlockablePlan = unlockRules.hasOwnProperty(item.plan_name);

    // Check if plan is unlocked
    const requiredPosts = unlockRules[item.plan_name] || 0;
    const isUnlocked = isUnlockablePlan && userPostCount >= requiredPosts;

    // Calculate progress percentage
    const progressPercentage = isUnlockablePlan
        ? Math.min((userPostCount / requiredPosts) * 100, 100)
        : 0;

    // Check if user already has this plan active
    const isCurrentPlan = User?.subscription?.plan === item._id;

    const selectHandler = (e) => {
        e.preventDefault();
        authInstance
            .post('/api/user/check_out/stripe-checkout', {
                subscriptionPlanId: item._id,
                userId: User._id
            })
            .then((response) => {
                window.location.replace(response.data.url);
            })
            .catch((err) => {
                console.log(err);
            });
    };

    // Handle activating unlocked plan
    const handleActivateUnlockedPlan = async (e) => {
        e.preventDefault();

        if (isActivating) return;

        setIsActivating(true);

        try {
            const response = await authInstance.post('/api/user/subscription_plans/activate_unlocked_plan', {
                userId: User._id,
                planId: item._id,
                planName: item.plan_name
            });

            if (response.data.success) {
                toast.success(`${item.plan_name} activated successfully! 🎉`);

                // Refresh page after 1.5 seconds
                setTimeout(() => {
                    window.location.reload();
                }, 1500);
            }
        } catch (error) {
            console.error(error);
            if (error.response?.data?.message) {
                toast.error(error.response.data.message);
            } else {
                toast.error("Failed to activate plan");
            }
        } finally {
            setIsActivating(false);
        }
    };

    // Render unlock badge
    const renderUnlockBadge = () => {
        if (!isUnlockablePlan) return null;

        if (isUnlocked) {
            return (
                <div className={Style.unlock_badge_unlocked}>
                    <FaUnlock /> FREE UNLOCKED
                </div>
            );
        } else {
            return (
                <div className={Style.unlock_badge_locked}>
                    <FaLock /> LOCKED
                </div>
            );
        }
    };

    // Render progress bar
    const renderProgressBar = () => {
        if (!isUnlockablePlan || isUnlocked) return null;

        return (
            <div className={Style.progress_section}>
                <div className={Style.progress_text}>
                    <span className={Style.progress_label}>
                        {userPostCount} / {requiredPosts} posts to unlock
                    </span>
                    <span className={Style.progress_percent}>
                        {Math.floor(progressPercentage)}%
                    </span>
                </div>
                <div className={Style.progress_bar_container}>
                    <div
                        className={Style.progress_bar_fill}
                        style={{ width: `${progressPercentage}%` }}
                    />
                </div>
            </div>
        );
    };



    const getFeaturesForPlan = (planItem) => {
        const planName = planItem?.plan_name || "";
        const lowerName = planName.toLowerCase();

        // Strictly defined features per user request
        if (lowerName.includes("silver")) {
            return [
                "2 Boosts/mo",
                "Unlock after 10 posts",
                "Render on Single Category Top",
                "30 Days Validity"
            ];
        } else if (lowerName.includes("golden")) {
            return [
                "3 Boosts/mo",
                "Unlock after 20 posts",
                "Render on Home Page",
                "30 Days Validity"
            ];
        } else if (lowerName.includes("platinum")) {
            return [
                "4 Boosts/mo",
                "Unlock after 40 posts",
                "Render on Home Page Top",
                "30 Days Validity"
            ];
        }

        // Default/Free plan or others
        return [
            "Standard Access"
        ];
    };

    const displayFeatures = getFeaturesForPlan(item);

    // Regular Plan Render (Golden, Platinum, Free)
    return (
        <div className={Style.item_wrap}>
            <div className={Style.wrapper}>
                {renderUnlockBadge()}

                <div className={Style.top}>
                    <div className={Style.row1}>
                        <h3>{item?.plan_name}</h3>
                        {item?.popular && <h5>Popular</h5>}
                    </div>

                    {/* Pricing Section */}
                    {!isUnlocked ? (
                        <>
                            <div className={Style.row2}>
                                <h1>
                                    <PiCurrencyInrBold /> {item?.monthly_pricing}
                                </h1>
                                <span> / </span>
                                <h4>Month per user</h4>
                            </div>
                            {item?.discount !== "" && (
                                <div className={Style.row3}>
                                    <p>{item?.discount}% Discount</p>
                                </div>
                            )}
                        </>
                    ) : (
                        <div className={Style.unlocked_pricing}>
                            <h2 style={{ color: "#00C853", margin: "20px 0" }}>
                                🎉 Unlocked - FREE for 30 days!
                            </h2>
                        </div>
                    )}

                    {/* Progress Bar */}
                    {renderProgressBar()}

                    {/* Action Button */}
                    {item?.monthly_pricing >= 0 && (
                        <div className={Style.row4}>
                            {isUnlocked ? (
                                <button
                                    className={Style.use_plan_btn}
                                    onClick={handleActivateUnlockedPlan}
                                    disabled={isActivating || isCurrentPlan}
                                >
                                    {isActivating
                                        ? '⏳ Activating...'
                                        : isCurrentPlan
                                            ? '✅ Active Plan'
                                            : '✨ Activate Plan'}
                                </button>
                            ) : (
                                <RazorpayButton plan={item} paid={item.monthly_pricing > 0} />
                            )}
                        </div>
                    )}
                </div>

                {/* Features Section */}
                <div className={Style.bottom}>
                    <div className={Style.title}>
                        <h2>Features</h2>
                    </div>
                    <div className={Style.row}>
                        {displayFeatures.map((feature, index) => {
                            return (
                                <div className={Style.item} key={index}>
                                    <span>
                                        <TiTick />
                                    </span>
                                    <p>{feature}</p>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Plancard;